import { BASE_URL } from 'api/constants';
import { getClientToken } from 'lib/auth/token';

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

export function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  if (error.status === 404 || error.status === 400) {
    return true;
  }

  if (error.status === 500) {
    try {
      const parsed = JSON.parse(error.message) as { success?: boolean };
      return parsed?.success === false;
    } catch {
      return false;
    }
  }

  return false;
}

type ParamValue = string | number | boolean | undefined | null;

export type ApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  params?: Record<string, ParamValue>;
  body?: unknown;
  headers?: Record<string, string>;
  auth?: boolean;
};

function joinBase(path: string) {
  const base = BASE_URL?.replace(/\/$/, '') ?? '';
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${base}/${normalizedPath}`;
}

function buildUrl(path: string, params?: Record<string, ParamValue>) {
  const url = joinBase(path);

  if (!params) {
    return url;
  }

  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });

  const query = search.toString();
  return query ? `${url}?${query}` : url;
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { method = 'GET', params, body, headers = {}, auth = false } = options;

  const requestHeaders: Record<string, string> = { ...headers };

  if (auth && typeof window !== 'undefined') {
    const token = getClientToken();
    if (token) {
      requestHeaders.Authorization = `Token ${token}`;
    }
  }

  let requestBody: BodyInit | undefined;

  if (body instanceof FormData) {
    requestBody = body;
  } else if (body !== undefined) {
    requestHeaders['Content-Type'] = 'application/json';
    requestBody = JSON.stringify(body);
  }

  const response = await fetch(buildUrl(path, params), {
    method,
    headers: requestHeaders,
    body: requestBody,
  });

  if (!response.ok) {
    const message = await response.text().catch(() => response.statusText);
    throw new ApiError(response.status, message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, ParamValue>,
  options?: Omit<ApiRequestOptions, 'method' | 'params' | 'body'>
) {
  return apiRequest<T>(path, { ...options, method: 'GET', params });
}

export async function apiGetSafe<T>(
  path: string,
  params?: Record<string, ParamValue>,
  options?: Omit<ApiRequestOptions, 'method' | 'params' | 'body'>
): Promise<T | null> {
  try {
    return await apiGet<T>(path, params, options);
  } catch (error) {
    if (isNotFoundError(error)) {
      return null;
    }

    throw error;
  }
}
